import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Copy,
  Eye,
  Loader2,
  FileText,
  Receipt,
  CreditCard,
  AlertCircle,
  Check,
  Code,
  Info,
  Star,
  StarOff,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template type icons
const templateTypeIcons = {
  quote_sent: FileText,
  invoice_sent: Receipt,
  payment_received: CreditCard,
  payment_reminder: AlertCircle,
};

// Template type colors
const templateTypeColors = {
  quote_sent: 'bg-blue-100 text-blue-700',
  invoice_sent: 'bg-emerald-100 text-emerald-700',
  payment_received: 'bg-green-100 text-green-700',
  payment_reminder: 'bg-amber-100 text-amber-700',
};

export default function EmailTemplates() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Form states
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    template_type: '',
    name: '',
    subject: '',
    html_content: '',
    text_content: '',
    is_default: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/api/email-templates`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/email-templates/types`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTemplates(templatesRes.data);
      setTemplateTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      template_type: '',
      name: '',
      subject: '',
      html_content: '',
      text_content: '',
      is_default: false,
    });
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      template_type: template.template_type,
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      text_content: template.text_content || '',
      is_default: template.is_default,
    });
    setShowEditDialog(true);
  };

  const handleDelete = (template) => {
    setDeletingTemplate(template);
    setShowDeleteDialog(true);
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(
        `${API_URL}/api/email-templates/${template.id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template duplicated successfully');
      fetchData();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/email-templates/preview`,
        {
          template_type: template.template_type,
          name: template.name,
          subject: template.subject,
          html_content: template.html_content,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewData(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Error previewing template:', error);
      toast.error('Failed to preview template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await axios.put(
        `${API_URL}/api/email-templates/${template.id}`,
        { is_default: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template set as default');
      fetchData();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error('Failed to set as default');
    }
  };

  const saveTemplate = async (isNew = true) => {
    if (!formData.template_type || !formData.name || !formData.subject || !formData.html_content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await axios.post(
          `${API_URL}/api/email-templates`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Template created successfully');
        setShowCreateDialog(false);
      } else {
        await axios.put(
          `${API_URL}/api/email-templates/${editingTemplate.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Template updated successfully');
        setShowEditDialog(false);
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/email-templates/${deletingTemplate.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template deleted');
      setShowDeleteDialog(false);
      setDeletingTemplate(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const getTypeInfo = (typeCode) => {
    return templateTypes.find(t => t.type_code === typeCode);
  };

  const filteredTemplates = selectedType === 'all' 
    ? templates 
    : templates.filter(t => t.template_type === selectedType);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Mail className="h-8 w-8 text-primary" />
            Email Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Customize email templates for quotes, invoices, and payment notifications
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="create-template-btn">
          <Plus className="mr-2 h-4 w-4" />
          Create Template
        </Button>
      </div>

      {/* Template Types Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {templateTypes.map((type) => {
          const Icon = templateTypeIcons[type.type_code] || FileText;
          const colorClass = templateTypeColors[type.type_code] || 'bg-gray-100 text-gray-700';
          const count = templates.filter(t => t.template_type === type.type_code).length;
          
          return (
            <Card 
              key={type.type_code}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedType === type.type_code ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedType(selectedType === type.type_code ? 'all' : type.type_code)}
              data-testid={`filter-${type.type_code}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className={`p-2 rounded-lg ${colorClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <Badge variant="secondary">{count}</Badge>
                </div>
                <h3 className="font-medium mt-2">{type.name}</h3>
                <p className="text-xs text-muted-foreground">
                  {type.has_custom_template ? 'Customized' : 'Using default'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Templates List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Templates</CardTitle>
              <CardDescription>
                {selectedType === 'all' 
                  ? `${templates.length} template${templates.length !== 1 ? 's' : ''} total`
                  : `Showing ${filteredTemplates.length} ${getTypeInfo(selectedType)?.name || selectedType} template${filteredTemplates.length !== 1 ? 's' : ''}`
                }
              </CardDescription>
            </div>
            {selectedType !== 'all' && (
              <Button variant="outline" size="sm" onClick={() => setSelectedType('all')}>
                Show All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No templates yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first email template to customize how your emails look
              </p>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Create Template
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTemplates.map((template) => {
                const Icon = templateTypeIcons[template.template_type] || FileText;
                const colorClass = templateTypeColors[template.template_type] || 'bg-gray-100 text-gray-700';
                
                return (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    data-testid={`template-item-${template.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{template.name}</h4>
                          {template.is_default && (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          )}
                          {!template.is_active && (
                            <Badge variant="outline" className="text-muted-foreground">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{template.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {getTypeInfo(template.template_type)?.name || template.template_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePreview(template)}
                        title="Preview"
                        data-testid={`preview-template-${template.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(template)}
                        title="Edit"
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDuplicate(template)}
                        title="Duplicate"
                        data-testid={`duplicate-template-${template.id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {!template.is_default && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSetDefault(template)}
                          title="Set as default"
                          data-testid={`default-template-${template.id}`}
                        >
                          <StarOff className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(template)}
                        title="Delete"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Variables Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Available Variables
          </CardTitle>
          <CardDescription>
            Use these variables in your templates. They'll be replaced with actual values when emails are sent.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              { var: 'customer_name', desc: 'Customer name' },
              { var: 'quote_number', desc: 'Quote number' },
              { var: 'invoice_number', desc: 'Invoice number' },
              { var: 'total_amount', desc: 'Total amount' },
              { var: 'balance_due', desc: 'Balance due' },
              { var: 'due_date', desc: 'Due date' },
              { var: 'payment_amount', desc: 'Payment amount' },
              { var: 'business_name', desc: 'Your business name' },
              { var: 'view_link', desc: 'View document link' },
              { var: 'pay_link', desc: 'Pay invoice link' },
              { var: 'portal_link', desc: 'Customer portal link' },
              { var: 'custom_message', desc: 'Custom message' },
            ].map(({ var: v, desc }) => (
              <div key={v} className="p-2 bg-muted rounded text-sm">
                <code className="text-primary font-mono">{`{{${v}}}`}</code>
                <p className="text-xs text-muted-foreground mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          setEditingTemplate(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showCreateDialog ? 'Create Email Template' : 'Edit Email Template'}
            </DialogTitle>
            <DialogDescription>
              {showCreateDialog 
                ? 'Create a custom email template for your business communications'
                : 'Update your email template settings and content'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Template Type (only for create) */}
            {showCreateDialog && (
              <div className="space-y-2">
                <Label>Template Type *</Label>
                <Select
                  value={formData.template_type}
                  onValueChange={(value) => setFormData({ ...formData, template_type: value })}
                >
                  <SelectTrigger data-testid="template-type-select">
                    <SelectValue placeholder="Select template type" />
                  </SelectTrigger>
                  <SelectContent>
                    {templateTypes.map((type) => (
                      <SelectItem key={type.type_code} value={type.type_code}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.template_type && (
                  <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium">Available variables:</p>
                      <p className="font-mono text-xs">
                        {getTypeInfo(formData.template_type)?.variables.map(v => `{{${v}}}`).join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Template Name */}
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Professional Quote Email"
                data-testid="template-name-input"
              />
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label>Email Subject *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Your Quote #{{quote_number}} from {{business_name}}"
                data-testid="template-subject-input"
              />
            </div>

            {/* HTML Content */}
            <div className="space-y-2">
              <Label>HTML Content *</Label>
              <Textarea
                value={formData.html_content}
                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                placeholder="Enter your HTML email content here..."
                className="min-h-[200px] font-mono text-sm"
                data-testid="template-html-input"
              />
            </div>

            {/* Plain Text Content */}
            <div className="space-y-2">
              <Label>Plain Text Content (Optional)</Label>
              <Textarea
                value={formData.text_content}
                onChange={(e) => setFormData({ ...formData, text_content: e.target.value })}
                placeholder="Plain text version for email clients that don't support HTML..."
                className="min-h-[100px]"
                data-testid="template-text-input"
              />
            </div>

            {/* Set as Default */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Set as default template for this type
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveTemplate(showCreateDialog)}
              disabled={saving}
              data-testid="save-template-btn"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              {showCreateDialog ? 'Create Template' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingTemplate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Preview with sample data. Variables are replaced with example values.
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">Subject</Label>
                <p className="font-medium">{previewData.subject}</p>
              </div>
              
              <Tabs defaultValue="html">
                <TabsList>
                  <TabsTrigger value="html">HTML Preview</TabsTrigger>
                  <TabsTrigger value="source">Source</TabsTrigger>
                </TabsList>
                <TabsContent value="html" className="border rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={previewData.html_content}
                    className="w-full h-[400px] bg-white"
                    title="Email Preview"
                  />
                </TabsContent>
                <TabsContent value="source">
                  <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[400px] text-sm">
                    {previewData.html_content}
                  </pre>
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowPreviewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
